import { apiRequest } from '../lib/api-client';

/** Single sign-on and provisioning. */

export interface SsoConnection {
  id: string;
  label: string;
  issuer: string;
  client_id: string;
  client_secret_hint: string;
  domains: string[];
  auto_provision: boolean;
  default_role_key: string | null;
  enabled: boolean;
  redirect_uri: string;
  sign_in_url: string;
  created_at: string;
}

export interface SsoConnectionInput {
  label: string;
  issuer: string;
  clientId: string;
  clientSecret?: string;
  domains: string[];
  autoProvision: boolean;
  defaultRoleKey: string | null;
  enabled: boolean;
}

export function listSsoConnections(): Promise<{ connections: SsoConnection[] }> {
  return apiRequest('/v1/settings/sso/connections');
}

export function createSsoConnection(input: SsoConnectionInput): Promise<SsoConnection> {
  return apiRequest('/v1/settings/sso/connections', { method: 'POST', body: input });
}

export function updateSsoConnection(
  id: string,
  input: Partial<SsoConnectionInput>,
): Promise<SsoConnection> {
  return apiRequest(`/v1/settings/sso/connections/${id}`, { method: 'PATCH', body: input });
}

export function deleteSsoConnection(id: string): Promise<void> {
  return apiRequest(`/v1/settings/sso/connections/${id}`, { method: 'DELETE' });
}

export interface SsoIdentity {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  subject: string;
  last_login_at: string | null;
}

export function listSsoIdentities(): Promise<{ identities: SsoIdentity[] }> {
  return apiRequest('/v1/settings/sso/identities');
}

export interface ScimToken {
  id: string;
  label: string;
  token_hint: string;
  default_role_key: string;
  last_used_at: string | null;
  created_at: string;
  base_url: string;
}

export function listScimTokens(): Promise<{ tokens: ScimToken[]; base_url: string }> {
  return apiRequest('/v1/settings/scim/tokens');
}

/** The only response that ever carries the token itself. */
export function createScimToken(input: {
  label: string;
  defaultRoleKey: string;
}): Promise<ScimToken & { token: string }> {
  return apiRequest('/v1/settings/scim/tokens', { method: 'POST', body: input });
}

export function revokeScimToken(id: string): Promise<void> {
  return apiRequest(`/v1/settings/scim/tokens/${id}`, { method: 'DELETE' });
}

export interface SignInOption {
  connection_id: string;
  label: string;
}

/** Unauthenticated: the sign-in screen asking which company an address belongs to. */
export function discoverSso(email: string): Promise<{ options: SignInOption[] }> {
  return apiRequest('/v1/auth/sso/discover', { method: 'POST', body: { email } });
}

export function startSso(
  connectionId: string,
  returnTo?: string,
): Promise<{ authorization_url: string; expires_in: number }> {
  return apiRequest(`/v1/auth/sso/${connectionId}/start`, {
    method: 'POST',
    body: returnTo ? { returnTo } : {},
  });
}
